import {useEffect, useState, useCallback, useMemo} from 'react';
import styled from 'styled-components';

// import {CalendarView} from '@stencil/dcalendar';
import {forwardRef} from 'react';
import {gaCheckinDateDifference} from '@stencil/hotels-utils/src/date-utils/date-utils';
import addDays from '@stencil/date-utils/src/addDays';
import {maxAllowedRoomNights} from '@stencil/hotels-config/src/config/app-config';
import {CalendarViewNew} from '@stencil/dcalendar';
import {useDispatch, useSelector} from 'react-redux';

import {getClassName, CalendarModes} from './CalendarBlockUtils';

Object.freeze(CalendarModes);

const CalendarBlockOuterWrapper = styled.div(
  props => `
    display: flex;
    background-color: ${props.theme.colors.goWhite.base};
    border-radius: ${props.theme.misc.borderRadius.base};
    box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.16);
    position: absolute;
    top: ${props.isSearchWidget ? '6.5rem' : '8rem'};
    z-index: ${props.theme.misc.zIndex.xlarge};
    border-radius: 1.2rem;
    ${Object.keys(props.styles)
      .map(key => `${key}:${props.styles[key]};`)
      .join('')}
  `,
);

const CalendarBlock = forwardRef(
  (
    props: {
      isSearchWidget;
      onSelection;
      updateQueryCaller;
      checkin;
      checkout;
      gaHandler?;
      styles?;
      mode;
      innerCalendarCustomStyles?;
    },
    ref,
  ) => {
    const {
      isSearchWidget,
      onSelection,
      updateQueryCaller,
      checkin,
      checkout,
      gaHandler,
      styles = {},
      mode,
      innerCalendarCustomStyles = {},
    } = props;
    const dispatch = useDispatch();

    const [checkInDate = checkin?.date || null, setCheckInDate] = useState();
    const [checkOutDate = checkout?.date || null, setCheckOutDate] = useState();
    const [currentMode, updateCurrentMode] = useState(() =>
      mode === 'checkout' ? CalendarModes.SELECTING_CHECKOUT : CalendarModes.SELECTING_CHECKIN,
    );

    useEffect(() => {
      if (mode === 'checkout') {
        updateCurrentMode(CalendarModes.SELECTING_CHECKOUT);
      } else if (mode === 'checkin') {
        updateCurrentMode(CalendarModes.SELECTING_CHECKIN);
      }
    }, [mode]);

    const dateOnClickHandler = useCallback(
      dateObj => {
        const isSelectingCheckin = currentMode === CalendarModes.SELECTING_CHECKIN;
        const dateObjTime = dateObj.getTime();
        const checkInDateTime = checkInDate?.getTime?.() || 0;

        const handleCheckinSelection = () => {
          setCheckInDate(dateObj);
          setCheckOutDate(dateObj);
          updateCurrentMode(CalendarModes.SELECTING_CHECKOUT);

          if (typeof gaHandler === 'function') {
            gaHandler('calendarClicked', gaCheckinDateDifference(checkin));
          }
        };

        const handleCheckoutSelection = () => {
          setCheckOutDate(dateObj);

          if (checkInDate) {
            updateQueryCaller({
              checkin: {...checkin, date: checkInDate},
              checkout: {...checkout, date: dateObj},
            });
            onSelection(checkInDate, dateObj, false);
          } else {
            updateCurrentMode(CalendarModes.SELECTING_CHECKIN);
          }
        };

        if (dateObjTime <= checkInDateTime || !checkInDateTime || isSelectingCheckin) {
          handleCheckinSelection();
        } else {
          handleCheckoutSelection();
        }
      },
      [
        currentMode,
        checkInDate,
        checkOutDate,
        gaHandler,
        gaCheckinDateDifference,
        checkin,
        checkout,
        updateCurrentMode,
        setCheckInDate,
        setCheckOutDate,
        updateQueryCaller,
        onSelection,
      ],
    );

    const todaysDate = useMemo(() => new Date(), []);

    const maxDateAllowed = useMemo(() => {
      // Create a new date object with the current date and time
      const date = new Date();

      // Add 12 months to the current date
      date.setFullYear(date.getFullYear(), date.getMonth() + 12);

      // Conditionally adjust the date for SELECTING_CHECKOUT mode
      if (currentMode === CalendarModes.SELECTING_CHECKOUT) {
        date.setDate(date.getDate() + maxAllowedRoomNights);
      }

      return date;
    }, [currentMode, maxAllowedRoomNights]);

    const renderDateItem = useCallback(
      ({value, dateObj}) => {
        if (!value) {
          return (
            <li>
              <span></span>
            </li>
          );
        }

        // Normalize the dateObj to the start of the day
        dateObj.setHours(0, 0, 0, 0);
        const dateObjTime = dateObj.getTime();

        // Normalize today's date to the start of the day
        const normalizedToday = new Date(todaysDate);
        normalizedToday.setHours(0, 0, 0, 0);
        const todaysDateTime = normalizedToday.getTime();

        // Determine if the date is valid based on the current mode
        let isValid = true;
        if (currentMode === CalendarModes.SELECTING_CHECKIN) {
          isValid = dateObjTime >= todaysDateTime && dateObjTime < maxDateAllowed.getTime();
        } else if (currentMode === CalendarModes.SELECTING_CHECKOUT) {
          const normalizedCheckInDate = checkInDate ? new Date(checkInDate) : new Date();
          normalizedCheckInDate.setHours(0, 0, 0, 0);
          const checkInDateTime = normalizedCheckInDate.getTime();
          const checkinLimitTime = addDays(normalizedCheckInDate, 20).getTime();
          isValid = dateObjTime >= checkInDateTime && dateObjTime < checkinLimitTime;
        }

        // Check for holidays
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth();
        const date = dateObj.getDate();

        return (
          <li
            style={
              isValid
                ? {color: 'black', height: '5rem'}
                : {opacity: '0.4', color: 'grey', height: '5rem'}
            }
            onClick={() => isValid && dateOnClickHandler(dateObj)}
            className={`date_is_selectable_${isValid}`}
            data-testid={`date_li_${date}_${month}_${year}`}
          >
            <span
              data-testid={`date_${date}_${month}_${year}`}
              className={getClassName(dateObj, checkInDate, checkOutDate)}
            >
              {value}
            </span>
          </li>
        );
      },
      [
        getClassName,
        checkInDate,
        checkOutDate,
        currentMode,
        maxDateAllowed,
        todaysDate,
        dateOnClickHandler,
      ],
    );

    return (
      <CalendarBlockOuterWrapper
        ref={ref}
        isSearchWidget={isSearchWidget}
        styles={styles}
        data-testid="CalendarBlockOuterWrapper"
      >
        <CalendarViewNew
          dateConfigForMonthToBeDisplayed={checkInDate || todaysDate}
          minDateAllowed={todaysDate}
          maxDateAllowed={maxDateAllowed}
          renderDateItem={renderDateItem}
          customStyles={innerCalendarCustomStyles}
        />
      </CalendarBlockOuterWrapper>
    );
  },
);

export default CalendarBlock;
